/**
 * Supplier Performance Tracker Widget
 * =====================================
 * Tracks supplier on-time delivery, quality scores,
 * and price trend alerts.
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Truck, Package, AlertTriangle, TrendingUp, TrendingDown,
  ChevronRight, Star, Clock, DollarSign, CheckCircle, XCircle
} from 'lucide-react';

const statusStyles = {
  excellent: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-500' },
  good: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500' },
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-500' },
};

const SupplierPerformanceTracker = ({
  industry = 'hospitality',
  data = null,
  className = '',
  compact = false,
  onViewDetails,
  onContactSupplier
}) => {
  const [showAll, setShowAll] = useState(false);
  // Real data only — never render placeholder/demo suppliers.
  const suppliers = Array.isArray(data) ? data : [];
  const hasData = suppliers.length > 0;

  if (!hasData) {
    return (
      <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06)] ${compact ? 'p-5' : 'p-6'} ${className}`} data-testid="supplier-tracker-empty">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 tracking-tight">Suppliers</h3>
        </div>
        <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-2xl bg-slate-50/60 border border-dashed border-slate-200">
          <Package className="w-8 h-8 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-700">No supplier data yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-xs">We'll surface real supplier on-time %, quality scores and spend from your accounting integration (vendor bills) once connected — no placeholder numbers.</p>
        </div>
      </div>
    );
  }

  const displayedSuppliers = compact 
    ? suppliers.slice(0, 2) 
    : (showAll ? suppliers : suppliers.slice(0, 4));
  
  const avgOnTime = suppliers.length ? Math.round(suppliers.reduce((sum, s) => sum + s.onTimeRate, 0) / suppliers.length) : 0;
  const avgQuality = suppliers.length ? Math.round(suppliers.reduce((sum, s) => sum + s.qualityScore, 0) / suppliers.length) : 0;
  const criticalSuppliers = suppliers.filter(s => s.status === 'critical' || s.status === 'warning').length;
  const totalSpend = suppliers.reduce((sum, s) => sum + s.monthlySpend, 0);

  if (compact) {
    return (
      <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} data-testid="supplier-tracker-compact">
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Truck className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">Suppliers</h4>
            </div>
            {criticalSuppliers > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100 whitespace-nowrap">{criticalSuppliers} issues</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col items-center justify-center p-3 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100">
              <span className="text-xl font-bold text-emerald-700">{avgOnTime}%</span>
              <span className="text-[10px] text-emerald-600 font-medium">On-Time Rate</span>
            </div>
            <div className="flex flex-col items-center justify-center p-3 rounded-2xl bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border border-blue-100">
              <span className="text-xl font-bold text-blue-700">{avgQuality}%</span>
              <span className="text-[10px] text-blue-600 font-medium">Quality Score</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} data-testid="supplier-tracker-widget">
      {/* Background gradient orb */}
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-indigo-100/40 to-purple-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Suppliers</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 whitespace-nowrap">{suppliers.length} active</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Manage <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

      {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="text-center p-2 rounded-xl bg-emerald-50 border border-emerald-200">
          <p className="text-lg font-bold text-emerald-700">{avgOnTime}%</p>
          <p className="text-[10px] text-emerald-600">On-Time Avg</p>
        </div>
        <div className="text-center p-2 rounded-xl bg-blue-50 border border-blue-200">
          <p className="text-lg font-bold text-blue-700">{avgQuality}%</p>
          <p className="text-[10px] text-blue-600">Quality Avg</p>
        </div>
        <div className="text-center p-2 rounded-xl bg-violet-50 border border-violet-200">
          <p className="text-lg font-bold text-violet-700">${(totalSpend / 1000).toFixed(0)}K</p>
          <p className="text-[10px] text-violet-600">MTD Spend</p>
        </div>
        <div className={`text-center p-2 rounded-xl ${criticalSuppliers > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} border`}>
          <p className={`text-lg font-bold ${criticalSuppliers > 0 ? 'text-red-700' : 'text-gray-700'}`}>{criticalSuppliers}</p>
          <p className={`text-[10px] ${criticalSuppliers > 0 ? 'text-red-600' : 'text-gray-600'}`}>Issues</p>
        </div>
      </div>

      {/* Supplier List */}
      <div className="space-y-2">
        {displayedSuppliers.map(supplier => {
          const style = statusStyles[supplier.status];
          const priceUp = supplier.priceTrend.startsWith('+');
          const priceHighAlert = parseFloat(supplier.priceTrend) > 5;
          
          return (
            <div 
              key={supplier.id} 
              className={`p-3 rounded-xl ${style.bg} border ${style.border} group hover:shadow-md transition-all`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{supplier.name}</h4>
                    <Badge className={`text-[9px] ${style.badge} text-white`}>{supplier.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-500">{supplier.category} • Last: {supplier.lastDelivery}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-6 text-xs opacity-0 group-hover:opacity-100"
                  onClick={() => onContactSupplier?.(supplier)}
                >
                  Contact
                </Button>
              </div>
              
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="text-center p-1.5 rounded-lg bg-white/50">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-sm font-bold">{supplier.onTimeRate}%</span>
                  </div>
                  <p className="text-[9px] text-gray-500">On-Time</p>
                </div>
                <div className="text-center p-1.5 rounded-lg bg-white/50">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-3 h-3 text-amber-400" />
                    <span className="text-sm font-bold">{supplier.qualityScore}%</span>
                  </div>
                  <p className="text-[9px] text-gray-500">Quality</p>
                </div>
                <div className={`text-center p-1.5 rounded-lg ${priceHighAlert ? 'bg-red-100' : 'bg-white/50'}`}>
                  <div className="flex items-center justify-center gap-1">
                    {priceUp ? (
                      <TrendingUp className={`w-3 h-3 ${priceHighAlert ? 'text-red-500' : 'text-amber-500'}`} />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-emerald-500" />
                    )}
                    <span className={`text-sm font-bold ${priceHighAlert ? 'text-red-700' : priceUp ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {supplier.priceTrend}
                    </span>
                  </div>
                  <p className="text-[9px] text-gray-500">Price Trend</p>
                </div>
                <div className="text-center p-1.5 rounded-lg bg-white/50">
                  <div className="flex items-center justify-center gap-1">
                    <DollarSign className="w-3 h-3 text-gray-400" />
                    <span className="text-sm font-bold">${(supplier.monthlySpend / 1000).toFixed(1)}K</span>
                  </div>
                  <p className="text-[9px] text-gray-500">MTD Spend</p>
                </div>
              </div>
              
              {supplier.issuesThisMonth > 0 && (
                <div className="flex items-center gap-2 mt-2 p-1.5 rounded-lg bg-white/50">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs text-gray-700">{supplier.issuesThisMonth} issue(s) this month</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {suppliers.length > 4 && !showAll && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full mt-3 text-xs"
          onClick={() => setShowAll(true)}
        >
          View All {suppliers.length} Suppliers <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}
      </div>
    </div>
  );
};

export default SupplierPerformanceTracker;
